import { Controller } from "@hotwired/stimulus"
export default class extends Controller {
    static targets = ["target"];
    static values = {
        max: { type: Number, default: 15 },     // degrés
        scale: { type: Number, default: 1.03 },// zoom
        easing: { type: Number, default: 0.24 },
        perspective: { type: Number, default: 900 }
    };

    connect() {
        this.targetEl = this.hasTargetTarget ? this.targetTarget : this.element.firstElementChild;
        if (!this.targetEl) return;

        // base styles
        this.element.style.perspective = `${this.perspectiveValue}px`;
        this.targetEl.style.transformStyle = "preserve-3d";
        this.targetEl.style.willChange = "transform";
        this.targetEl.style.transition = "transform 180ms ease";

        // state
        this._raf = null;
        this._hovering = false;
        this._currentX = 0;
        this._currentY = 0;
        this._targetX = 0;
        this._targetY = 0;

        // bind
        this._onEnter = this.enter.bind(this);
        this._onMove = this.move.bind(this);
        this._onLeave = this.leave.bind(this);

        this.element.addEventListener("mouseenter", this._onEnter, { passive: true });
        this.element.addEventListener("mousemove", this._onMove, { passive: true });
        this.element.addEventListener("mouseleave", this._onLeave, { passive: true });

        // mobile (optionnel)
        this.element.addEventListener("touchstart", this._onEnter, { passive: true });
        this.element.addEventListener("touchmove", this._onMove, { passive: true });
        this.element.addEventListener("touchend", this._onLeave, { passive: true });
    }

    disconnect() {
        this.element.removeEventListener("mouseenter", this._onEnter);
        this.element.removeEventListener("mousemove", this._onMove);
        this.element.removeEventListener("mouseleave", this._onLeave);
        this.element.removeEventListener("touchstart", this._onEnter);
        this.element.removeEventListener("touchmove", this._onMove);
        this.element.removeEventListener("touchend", this._onLeave);
        if (this._raf) cancelAnimationFrame(this._raf);
    }

    enter() {
        if (!this.targetEl) return;
        this._hovering = true;
        this.targetEl.style.transition = "transform 0ms";
    }

    leave() {
        if (!this.targetEl) return;
        this._hovering = false;
        this._targetX = 0;
        this._targetY = 0;
        this.targetEl.style.transition = "transform 180ms ease";
        this.applyTransform(0, 0);
    }

    move(e) {
        if (!this.targetEl) return;

        const point = e.touches?.[0] ?? e;
        const rect = this.element.getBoundingClientRect();

        const px = (point.clientX - rect.left) / rect.width;
        const py = (point.clientY - rect.top) / rect.height;

        const nx = (px - 0.5) * 2; // -1..1
        const ny = (py - 0.5) * 2; // -1..1

        const max = this.maxValue;
        this._targetX = this.clamp(-ny * max, -max, max); // rotateX
        this._targetY = this.clamp(nx * max, -max, max);  // rotateY

        if (!this._raf) this._raf = requestAnimationFrame(() => this.tick());
    }

    tick() {
        const easing = this.easingValue;

        this._currentX += (this._targetX - this._currentX) * easing;
        this._currentY += (this._targetY - this._currentY) * easing;

        this.applyTransform(this._currentX, this._currentY);

        const done =
            Math.abs(this._targetX - this._currentX) < 0.01 &&
            Math.abs(this._targetY - this._currentY) < 0.01;

        if (!done && this._hovering) {
            this._raf = requestAnimationFrame(() => this.tick());
        } else {
            this._raf = null;
        }
    }

    applyTransform(rx, ry) {
        this.targetEl.style.transform =
            `rotateX(${rx}deg) rotateY(${ry}deg) scale(${this.scaleValue})`;
    }

    clamp(v, min, max) {
        return Math.max(min, Math.min(max, v));
    }

}